import React from 'react';
import { ItineraryItem } from '../../types/Quotation';
import { formatDate } from '../../utils/date';
import '../../css/QuotationBuilder.css';

interface HotelTableProps {
  itinerary: ItineraryItem[];
  onRemove?: (dayIndex: number, hotelIndex: number) => void;
  hideActions?: boolean;
  showRoomType?: boolean;
}

const HotelTable: React.FC<HotelTableProps> = ({
  itinerary,
  onRemove,
  hideActions = false,
  showRoomType = false,
}) => {
  // Flatten and de-duplicate by hotel name across all days.
  const rows = (() => {
    const flat = itinerary.flatMap((day, dayIndex) =>
      (day.hotels || []).map((hotel, hotelIndex) => ({
        ...hotel,
        day: day.day,
        starRating: hotel.starRating || '0',
        dayIndex,
        hotelIndex,
      }))
    );
    const map = new Map<string, any>();
    flat.forEach(h => {
      const key = String((h as any).hotelId || h.name || '').toLowerCase();
      if (!key) return;
      const existing = map.get(key);
      const ci = (h as any).checkInDate;
      const co = (h as any).checkOutDate;
      if (!existing) {
        map.set(key, { ...h, checkInDate: ci, checkOutDate: co });
      } else {
        // merge dates (earliest CI, latest CO)
        const eci = existing.checkInDate;
        const eco = existing.checkOutDate;
        const minCI = eci && ci ? (new Date(eci) <= new Date(ci) ? eci : ci) : (eci || ci);
        const maxCO = eco && co ? (new Date(eco) >= new Date(co) ? eco : co) : (eco || co);
        map.set(key, { ...existing, checkInDate: minCI, checkOutDate: maxCO });
      }
    });
    return Array.from(map.values());
  })();

  if (!rows.length) return null;

  return (
    <table className="flight-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Star</th>
          {showRoomType && <th>Room Type</th>}
          <th>Check‑In Date</th>
          <th>Check‑Out Date</th>
          <th>Check‑In</th>
          <th>Check‑Out</th>
          <th>Image</th>
          {!hideActions && <th>Action</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((h, idx) => (
          <tr key={idx}>
            <td>{h.name}</td>
            <td>{h.starRating}★</td>
            {showRoomType && <td>{(h as any).roomType || '-'}</td>}
            <td>{(h as any).checkInDate ? formatDate((h as any).checkInDate) : '-'}</td>
            <td>{(h as any).checkOutDate ? formatDate((h as any).checkOutDate) : '-'}</td>
            <td>{h.checkin_time}</td>
            <td>{h.checkout_time}</td>
            <td>
              {h.thumbnail ? (
                <img
                  src={h.thumbnail}
                  alt={h.name}
                  style={{
                    maxWidth: '80px',
                    maxHeight: '60px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
              ) : (
                <span style={{ color: '#aaa' }}>No image</span>
              )}
            </td>
            {!hideActions && onRemove && (
              <td>
                <button onClick={() => onRemove(h.dayIndex, h.hotelIndex)}>×</button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default HotelTable;
